/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import { CATEGORY, FILTER_VALUES, FILTERING_RANGE, MULTI_FILTER_VALUES } from '../ISRConstants';
import {
    TFieldDataConfiguration,
    TFilterArrayItem, TIndexState,
    TUpsertISRProjectDataModelsInput
} from '../Pages/DefineModel/model';
import { getAssociatedAttributes, getCategoryTypeAndCategory } from '../Pages/DefineModel/ModelServices/ModalServices';
import { DeleteIcon } from "../../../../assests/GmsIcons";
import { toast } from 'Components/DesignSystem/Toast/useToast';
import ConfirmDialog from "./ConfirmDialog";
import { AppContext } from "../../../../Context/AppContext";
import { ISRContext } from "../Context/ISRContext";
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Radio from 'Components/DesignSystem/Radio/Radio';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import DsTooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';

type TMultiChoiceProps = {
    id: string;
    fieldDataConfiguration: TFieldDataConfiguration;
    setFieldDataConfiguration: React.Dispatch<React.SetStateAction<TFieldDataConfiguration>>;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo: TIndexState;
    hasRange?: boolean;
    isRangeField?: boolean;
}

const ConfigurationFilterComponent = ({ id, fieldDataConfiguration, setFieldDataConfiguration, userProjectDataModels, indexInfo, hasRange = false, isRangeField = false }: TMultiChoiceProps) => {
    const [attributesMap, setAttributesMap] = useState<Record<string, any[]>>({});
    const [showDialog, setShowDialog] = useState<any>(null);
    const [categoryData, setCategoryData] = useState([]);
    let appContext = useContext(AppContext);
    let { networkUserProjectDataModels } = useContext(ISRContext);
    let industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;

    const createMultiFilter = async () => {
        const updatedFieldDataConfiguration: TFieldDataConfiguration = { ...fieldDataConfiguration };
        if (!updatedFieldDataConfiguration.multiFilter) {
            updatedFieldDataConfiguration.multiFilter = { type: 'single', logic: 'allTrue' };
        }

        const dataModelFound = networkUserProjectDataModels.project.dataModels[indexInfo.dataModelIndex ?? -1];
        let subModelId: any = '';
        if (dataModelFound) {
            subModelId = dataModelFound.id;
        }
        if (!updatedFieldDataConfiguration.multiFilter?.filters) {
            updatedFieldDataConfiguration.multiFilter.filters = [];
        }
        const filters = fieldDataConfiguration?.multiFilter?.filters ?? [];
        let categoryId = '';
        if (fieldDataConfiguration.multiFilter?.type === 'single' && filters.length > 0) {
            if (!filters[0].categoryId) {
                toast.error('Please select category to continue');
                return;
            }
            categoryId = filters[0].categoryId;
        } else {
            if (dataModelFound) {
                subModelId = dataModelFound.id;
                let subModelCategories = dataModelFound.data.productCategories.map((e: any) => e.data.categories.data.map((category: any) => category.id)).flat(1);
                if (subModelCategories.length === 1) {
                    categoryId = subModelCategories[0];
                    await fetchAssociatedAttributes(categoryId);
                }
            }
        }
        updatedFieldDataConfiguration.multiFilter.filters.push({
            operator: '=',
            subModelId: subModelId ?? "",
            categoryId: categoryId,
            attributeId: ""
        });
        setFieldDataConfiguration(updatedFieldDataConfiguration);
    };

    const updateMultiFilterType = (type: string) => {
        const updatedFieldDataConfiguration: TFieldDataConfiguration = { ...fieldDataConfiguration };
        if (!updatedFieldDataConfiguration.multiFilter) {
            updatedFieldDataConfiguration.multiFilter = { type: type, logic: 'allTrue' };
        } else {
            updatedFieldDataConfiguration.multiFilter.type = type;
            updatedFieldDataConfiguration.multiFilter.filters = [];
        }
        setFieldDataConfiguration(updatedFieldDataConfiguration);
    };

    const updateMultiFilterLogic = async (logic: string) => {
        const updatedFieldDataConfiguration: TFieldDataConfiguration = { ...fieldDataConfiguration };
        if (updatedFieldDataConfiguration.multiFilter) {
            updatedFieldDataConfiguration.multiFilter.logic = logic;
            setFieldDataConfiguration(updatedFieldDataConfiguration);
        }
    };

    const handleCategoryDelete = async (index: number) => {
        const updatedFieldDataConfiguration: TFieldDataConfiguration = { ...fieldDataConfiguration };
        updatedFieldDataConfiguration.multiFilter?.filters?.splice(index, 1);
        setFieldDataConfiguration(updatedFieldDataConfiguration);
    };
    const handleFilterChange = async (filter: TFilterArrayItem, index: number, isCategory: boolean) => {
        const updatedFieldDataConfiguration: TFieldDataConfiguration = { ...fieldDataConfiguration };
        if (updatedFieldDataConfiguration.multiFilter?.filters) {
            const filters = updatedFieldDataConfiguration.multiFilter.filters;
            if (isCategory && fieldDataConfiguration.multiFilter?.type === 'multiple' && filters.length > 0) {
                const filterCombination = `${filter.subModelId}${filter.categoryId}`
                for (let item of filters) {
                    if (`${item.subModelId}${item.categoryId}` === filterCombination) {
                        toast.error('This filter combination already exists');
                        return;
                    }
                }
            }
            if (isCategory && fieldDataConfiguration.multiFilter?.type === 'single' && filters.length > 0) {
                for (let i = 0; i < updatedFieldDataConfiguration.multiFilter.filters.length; i++) {
                    if (updatedFieldDataConfiguration.multiFilter.filters[i].categoryId !== filter.categoryId) {
                        updatedFieldDataConfiguration.multiFilter.filters[i].categoryId = filter.categoryId;
                        updatedFieldDataConfiguration.multiFilter.filters[i].attributeId = '';
                    }
                }
            }

            updatedFieldDataConfiguration.multiFilter.filters[index] = filter;
            setFieldDataConfiguration(updatedFieldDataConfiguration);
            if (isCategory) {
                await fetchAssociatedAttributes(filter.categoryId);
            }
        }
    };

    const handleFilterRangeChange = (value: boolean) => {
        setFieldDataConfiguration((prevConfig) => ({ ...prevConfig, filterRange: value }));
    }

    const fetchAssociatedAttributes = async (categoryId: string) => {
        try {
            const responseData = await getAssociatedAttributes(categoryId);
            if (responseData?.done) {
                attributesMap[categoryId] = responseData?.data.sort((a: any, b: any) => {
                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                });
                setAttributesMap(attributesMap);
            }
        } catch (error) {
            console.log(error, 'Error');
        }
    }

    const fetchCategoryTypesAndCategories = async () => {
        try {
            setCategoryData([]);
            const response = await getCategoryTypeAndCategory(industryId ?? '');
            if (response?.done) {
                setCategoryData(response?.data);
            }
        } catch (e) {
            console.log('Error', e);
        }
    }

    const getProductCategoryCounts = (filterCategoryId: string, subModelId: string): { minCount: number; maxCount: number } => {
        if (!filterCategoryId) return { minCount: 0, maxCount: 1 };

        const dataModel = networkUserProjectDataModels?.project?.dataModels?.find(
            (dm: any) => dm.id === subModelId
        );
        if (!dataModel) return { minCount: 0, maxCount: 1 };

        const productCategory = dataModel.data?.productCategories?.find(
            (pc: any) => pc.data?.categories?.data?.some((cat: any) => cat.id === filterCategoryId)
        );

        return {
            minCount: productCategory?.data?.minCount ?? 0,
            maxCount: productCategory?.data?.maxCount ?? 1
        };
    };

    const getVdaTooltipContent = () => {
        return (
            <div>
                {/* No ink class: the DS Tooltip bubble sets `tw-text-ink-inverse` on its
                    content slot. The old `tw-text-white` overrode it — correct-looking in
                    light (the bubble is dark there) and invisible in dark, where the
                    inverse bubble is LIGHT. Inheriting themes both. */}
                <div className="tw-text-sm tw-font-normal">
                    Variant Defining Attribute
                </div>
                <div className="tw-text-sm tw-font-normal tw-mt-2">
                    when enabled, this field&apos;s selected value will be sent to the build as a configurable option along with the selected product
                </div>
            </div>
        );
    }

    useEffect(() => {
        fetchCategoryTypesAndCategories();
    }, []);

    useEffect(() => {
        if (fieldDataConfiguration?.multiFilter?.filters && fieldDataConfiguration?.multiFilter?.filters.length > 0) {
            fieldDataConfiguration?.multiFilter?.filters?.map((filter) => fetchAssociatedAttributes(filter.categoryId));
        }
    }, [fieldDataConfiguration?.multiFilter?.filters]);


    return (
        <div id={id} className='tw-mt-2 tw-mb-2'>
            <div id={`${id}-header`} className='tw-w-full tw-mb-auto tw-flex tw-items-center tw-justify-between'>
                <Typography variant="sub-title-label" as="p" id={`${id}-title`} className='tw-text-ink-secondary'>
                    {'Filter'}
                </Typography>
                {hasRange ? (
                    <div id={`${id}-range-container`} className='tw-flex tw-items-center tw-gap-2 tw-me-16'>
                        <Toggle
                            id={`${id}-range-toggle`}
                            data-testid={`${id}-range-toggle`}
                            label={FILTERING_RANGE}
                            checked={fieldDataConfiguration?.filterRange ?? false}
                            onChange={handleFilterRangeChange}
                        />
                    </div>
                ) : null}
            </div>

            {/*
                Two real radios sharing a name scoped to THIS instance. The name used
                to be the literal 'inline-radio-group' — page-global, so a second
                ConfigurationFilterComponent would have joined the first's group. Both
                labels also pointed `htmlFor` at ids that do not exist ('inline-radio',
                'inline-2-radio'), so clicking the words did nothing and neither input
                had an accessible name. DS Radio puts the input inside its own label,
                which removes the whole class of problem.
            */}
            <div
                id={`${id}-filter-type`}
                role="radiogroup"
                aria-label="Filter category mode"
                className="tw-flex tw-gap-4 tw-my-4"
            >
                <Radio
                    id={`${id}-single-radio`}
                    data-testid={`${id}-single-radio`}
                    name={`${id}-filter-type`}
                    value="single"
                    label="Single Category"
                    checked={fieldDataConfiguration.multiFilter?.type === 'single'}
                    onChange={() => {
                        if ((fieldDataConfiguration.multiFilter?.filters ?? []).length > 0) {
                            setShowDialog('single');
                            return;
                        }
                        updateMultiFilterType('single');
                    }}
                />
                <Radio
                    id={`${id}-multiple-radio`}
                    data-testid={`${id}-multiple-radio`}
                    name={`${id}-filter-type`}
                    value="multiple"
                    label="Multiple Category"
                    checked={fieldDataConfiguration.multiFilter?.type === 'multiple'}
                    onChange={() => {
                        if ((fieldDataConfiguration.multiFilter?.filters ?? []).length > 0) {
                            setShowDialog('multiple');
                            return;
                        }
                        updateMultiFilterType('multiple');
                    }}
                />
            </div>

            {
                fieldDataConfiguration?.multiFilter?.filters?.map((filter, filterIndex) => {
                    let categoryTypesToDisplay: any = [];
                    const dataModelFound = networkUserProjectDataModels.project.dataModels.filter((e: any) => e.id === filter.subModelId)
                    if (dataModelFound.length > 0) {
                        const dataModelToUpdate = dataModelFound[0];
                        let subModelCategories = [...dataModelToUpdate.data.productCategories].map((e) => e.data.categories.data.map((category: any) => category.id)).flat(1);
                        if (subModelCategories.length > 0) {
                            let allCategories = structuredClone(categoryData);
                            categoryTypesToDisplay = allCategories.map((item: any) => {
                                item.category = item.category.filter((e: any) => subModelCategories.includes(e.id))
                                return item;
                            }).filter((e: any) => e.category.length > 0);
                        }
                    }
                    const attributes = attributesMap[filter.categoryId] ?? [];
                    const { minCount, maxCount } = getProductCategoryCounts(filter.categoryId, filter.subModelId ?? '');
                    const isVdaDisabled = minCount > 1 || maxCount > 1;
                    /*
                        Every one of these four selects carried the SAME static testid
                        on every row — four ids for N rows, so `getByTestId` was
                        ambiguous the moment a second filter existed. They are
                        templated per row now; row 0 keeps the old static string so
                        existing specs still resolve.
                    */
                    const rowTestId = (suffix: string, legacy: string) =>
                        filterIndex === 0 ? legacy : `${id}-filter-${filterIndex}-${suffix}`;
                    return (
                        <div
                            key={filterIndex}
                            id={`${id}-filter-item-${filterIndex}`}
                            className='tw-flex tw-items-start tw-mt-2'
                        >
                            <div id={`${id}-filter-item-${filterIndex}-operator-container`} className='tw-w-[18%] tw-pe-2'>
                                <Dropdown
                                    id={`${id}-filter-item-${filterIndex}-operator-select`}
                                    data-testid={rowTestId('operator', 'components-configurationfiltercomponent-select-3')}
                                    label={'Operator'}
                                    placeholder="Select"
                                    options={[
                                        { value: 'Select', label: 'Select' },
                                        ...(isRangeField ? MULTI_FILTER_VALUES : FILTER_VALUES).map((item) => ({
                                            value: item.value,
                                            label: item.display,
                                        })),
                                    ]}
                                    value={filter?.operator ?? ''}
                                    onChange={(value) => handleFilterChange({
                                        ...filter,
                                        operator: value === 'Select' ? '' : String(value)
                                    }, filterIndex, false)}
                                    usePortal
                                />
                            </div>
                            <div id={`${id}-filter-item-${filterIndex}-submodel-container`} className='tw-w-[37%] tw-ms-4'>
                                <Dropdown
                                    id={`${id}-filter-item-${filterIndex}-submodel-select`}
                                    data-testid={rowTestId('submodel', 'components-configurationfiltercomponent-select-4')}
                                    label={'SubModel'}
                                    placeholder="Select Submodel"
                                    options={(networkUserProjectDataModels?.project.dataModels ?? []).map((item: any) => ({
                                        value: item.id ?? '',
                                        label: item.data.name,
                                    }))}
                                    value={filter?.subModelId ?? ''}
                                    disabled={fieldDataConfiguration.multiFilter?.type === 'single'}
                                    onChange={(value) => handleFilterChange({
                                        ...filter,
                                        subModelId: value === 'Select Submodel' ? '' : String(value),
                                        categoryId: '',
                                        attributeId: '',
                                    }, filterIndex, false)}
                                    usePortal
                                />
                            </div>
                            <div id={`${id}-filter-item-${filterIndex}-category-container`} className='tw-w-[37%] tw-ms-4'>
                                {/* `<optgroup label>` becomes DS Dropdown's `group` on
                                    each option — same grouping by category type, same
                                    order. */}
                                <Dropdown
                                    id={`${id}-filter-item-${filterIndex}-category-select`}
                                    data-testid={rowTestId('category', 'components-configurationfiltercomponent-select-5')}
                                    label={CATEGORY}
                                    placeholder="Select Category"
                                    options={categoryTypesToDisplay.flatMap((item: any) =>
                                        item.category.map((category: any) => ({
                                            value: category.id,
                                            label: category.name,
                                            group: item.name,
                                        })))}
                                    value={filter?.categoryId ?? ''}
                                    onChange={(value) => handleFilterChange({
                                        ...filter,
                                        categoryId: value === 'Select Category' ? '' : String(value),
                                        attributeId: ''
                                    }, filterIndex, true)}
                                    usePortal
                                />
                            </div>
                            <div id={`${id}-filter-item-${filterIndex}-attribute-container`} className='tw-w-[37%] tw-ms-4'>
                                {/* `searchable` (QA, 2026-10-05): a filter box (`…-search`)
                                    above the list. Client-side — attributesMap is already
                                    loaded for the category, so no onSearchChange. */}
                                <Dropdown
                                    searchable
                                    searchPlaceholder="Search attributes"
                                    id={`${id}-filter-item-${filterIndex}-attribute-select`}
                                    data-testid={rowTestId('attribute', 'components-configurationfiltercomponent-select-6')}
                                    label={'Attribute'}
                                    placeholder="Select Attribute"
                                    options={(attributes ?? []).map((item: any) => ({
                                        value: item.id,
                                        label: `${item.name}${item.type === 'Transformation' ? ' (Transformation)' : ''}`,
                                    }))}
                                    value={filter?.attributeId ?? ''}
                                    onChange={(value) => handleFilterChange({
                                        ...filter,
                                        attributeId: value === 'Select Attribute' ? '' : String(value)
                                    }, filterIndex, false)}
                                    usePortal
                                />
                            </div>
                            <div id={`${id}-filter-item-${filterIndex}-vda-toggle-container`} className='tw-w-[10%] tw-ms-4 tw-mt-2'>
                                <div className="tw-flex tw-items-center tw-gap-1">
                                    <Typography variant="body-primary" className="tw-text-ink-secondary">VDA</Typography>
                                    <InfoTooltip
                                        data-testid={`${id}-filter-${filterIndex}-vda-info`}
                                        placement="top"
                                        message={getVdaTooltipContent()}
                                    />
                                </div>
                                {/* `disabled` on the Tooltip rather than two copies of
                                    the same switch. The wrapper span is what carries
                                    the hover, which is what makes it work at all over
                                    a disabled control. */}
                                <DsTooltip
                                    content={'Cannot enable VDA when category count is greater than 1. Reduce the category count to enable VDA.'}
                                    disabled={!isVdaDisabled}
                                    placement="top"
                                >
                                    <span>
                                        <Toggle
                                            id={`${id}-filter-item-${filterIndex}-vda-toggle`}
                                            data-testid={rowTestId('vda-toggle', `${id}-vda-toggle`)}
                                            label=""
                                            aria-label="VDA"
                                            checked={filter?.vda ?? false}
                                            onChange={(checked) => handleFilterChange({
                                                ...filter,
                                                vda: checked,
                                            }, filterIndex, false)}
                                            disabled={isVdaDisabled}
                                        />
                                    </span>
                                </DsTooltip>
                            </div>
                            <div id={`${id}-filter-item-${filterIndex}-delete-container`} className='tw-w-[10%] tw-ms-4 tw-mt-3'>
                                <Button
                                    aria-label={`Delete filter ${filterIndex + 1}`}
                                    data-testid={rowTestId('delete', 'components-configurationfiltercomponent-button-7')}
                                    id={`${id}-filter-item-${filterIndex}-delete-btn`}
                                    variant="subtle"
                                    size="sm"
                                    icon={<DeleteIcon />}
                                    showIcon
                                    className='tw-mt-2'
                                    onClick={() => handleCategoryDelete(filterIndex)}
                                >
                                    {""}
                                </Button>
                            </div>
                        </div>
                    );
                })
            }
            <Button
                data-testid="components-configurationfiltercomponent-button-8"
                id={`${id}-add-filter-btn`}
                variant="tertiary"
                size="sm"
                className='tw-mt-4'
                onClick={createMultiFilter}
            >
                {'Add new filter'}
            </Button>
            {(fieldDataConfiguration.multiFilter?.filters?.length ?? 0) > 0 && fieldDataConfiguration.multiFilter?.type === 'single' ? (
                <div
                    id={`${id}-logic-selector`}
                    role="radiogroup"
                    aria-label="Filter logic"
                    className="tw-flex tw-gap-4 tw-mt-4 tw-mb-8"
                >
                    {/* Same story as the mode radios above: the name was the
                        page-global 'inline-radio-group-logic', and both labels pointed
                        `htmlFor` at that NAME rather than at an input id. */}
                    <Radio
                        id={`${id}-all-true-radio`}
                        data-testid={`${id}-all-true-radio`}
                        name={`${id}-filter-logic`}
                        value="allTrue"
                        label="All True"
                        checked={fieldDataConfiguration.multiFilter?.logic === 'allTrue'}
                        onChange={() => updateMultiFilterLogic('allTrue')}
                    />
                    <Radio
                        id={`${id}-any-true-radio`}
                        data-testid={`${id}-any-true-radio`}
                        name={`${id}-filter-logic`}
                        value="anyTrue"
                        label="Any True"
                        checked={fieldDataConfiguration.multiFilter?.logic === 'anyTrue'}
                        onChange={() => updateMultiFilterLogic('anyTrue')}
                    />
                </div>
            ) : null}
            {showDialog != null ? <ConfirmDialog
                id={`${id}-confirm-dialog`}
                title={'Alert'}
                message={'The filters you ve added will be cleared, are you sure you want to continue ?'}
                onClose={() => {
                    setShowDialog(null);
                }}
                onConfirm={() => {
                    updateMultiFilterType(showDialog)
                    setShowDialog(null);
                }}
            /> : null}
        </div>
    );
}

export default ConfigurationFilterComponent;
